export const REPORT_PERIOD_MAX_INCLUSIVE_DAYS = 93;
export const REPORT_PERIOD_HISTORY_YEARS = 3;

export const REPORT_PERIOD_INVALID_REASON = {
  OUT_OF_BOUNDS: 'out_of_bounds',
  START_AFTER_END: 'start_after_end',
  TOO_LONG: 'too_long',
} as const;

export type ReportPeriodBounds = {
  maxDate: Date;
  minDate: Date;
};

export type ReportPeriodInvalidReason =
  (typeof REPORT_PERIOD_INVALID_REASON)[keyof typeof REPORT_PERIOD_INVALID_REASON];

const getStartOfCalendarDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getCalendarDayNumber = (date: Date): number => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

const subtractCalendarYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  const month = result.getMonth();

  result.setFullYear(result.getFullYear() - years);

  if (result.getMonth() !== month) {
    result.setDate(0);
  }

  return result;
};

export const getReportPeriodDateBounds = (now = new Date()): ReportPeriodBounds => {
  const maxDate = getStartOfCalendarDay(now);

  return {
    maxDate,
    minDate: subtractCalendarYears(maxDate, REPORT_PERIOD_HISTORY_YEARS),
  };
};

export const getReportPeriodCalendarMaxDate = (startDate: Date | null, endDate: Date | null, maxDate: Date): Date => {
  const normalizedMaxDate = getStartOfCalendarDay(maxDate);

  if (!startDate || endDate) {
    return normalizedMaxDate;
  }

  const rangeMaxDate = getStartOfCalendarDay(startDate);
  rangeMaxDate.setDate(rangeMaxDate.getDate() + REPORT_PERIOD_MAX_INCLUSIVE_DAYS - 1);

  return rangeMaxDate < normalizedMaxDate ? rangeMaxDate : normalizedMaxDate;
};

export const getReportPeriodInvalidReason = (
  startDate: Date,
  endDate: Date,
  { maxDate, minDate }: ReportPeriodBounds
): ReportPeriodInvalidReason | null => {
  const start = getStartOfCalendarDay(startDate);
  const end = getStartOfCalendarDay(endDate);
  const normalizedMinDate = getStartOfCalendarDay(minDate);
  const normalizedMaxDate = getStartOfCalendarDay(maxDate);

  if (start > end) {
    return REPORT_PERIOD_INVALID_REASON.START_AFTER_END;
  }

  if (start < normalizedMinDate || end > normalizedMaxDate) {
    return REPORT_PERIOD_INVALID_REASON.OUT_OF_BOUNDS;
  }

  const inclusiveDays = (getCalendarDayNumber(end) - getCalendarDayNumber(start)) / (1000 * 60 * 60 * 24) + 1;

  return inclusiveDays > REPORT_PERIOD_MAX_INCLUSIVE_DAYS ? REPORT_PERIOD_INVALID_REASON.TOO_LONG : null;
};
